
import React, { useState } from 'react';
import { Phone, Send, LockKeyhole, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { sendPaymentByPhone, processUnverifiedSmsTransfer } from '@/utils/smsService';
import { formatPhoneNumber } from '@/utils/formatters';

interface QuickSmsTransferProps {
  className?: string;
}

const QuickSmsTransfer: React.FC<QuickSmsTransferProps> = ({ className = '' }) => {
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phone || !amount) {
      toast.error('Please enter both phone number and amount');
      return;
    }
    
    // Basic phone validation
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsLoading(true);
    try {
      // Attempt to send payment by SMS with enhanced AI and Stripe processing
      const paymentSent = await sendPaymentByPhone(phone, numAmount);
      
      // If payment fails, try unverified transfer
      if (!paymentSent) {
        const unverifiedTransfer = await processUnverifiedSmsTransfer(phone, numAmount);
        
        if (unverifiedTransfer) {
          toast.warning('Transfer sent to unverified phone with AI risk assessment', {
            description: 'The recipient will receive instructions to claim funds at bluetoothcash.net'
          });
          setIsSuccess(true);
        } else {
          toast.error('Transfer failed. Unable to process payment');
          return;
        }
      } else {
        toast.success('Transfer initiated successfully!', {
          description: `The recipient will receive an SMS with claim instructions for $${numAmount.toFixed(2)} at bluetoothcash.net`
        });
        setIsSuccess(true);
      }
      
      // Reset form after successful transfer
      setPhone('');
      setAmount('');
    } catch (error) {
      toast.error('Failed to send transfer', {
        description: 'An unexpected error occurred'
      });
      console.error('Transfer error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`p-6 bg-white rounded-2xl shadow-sm ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-blue-100 rounded-full">
          <Phone className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="font-semibold text-lg text-gray-900">Quick SMS Transfer</h2>
      </div>

      {isSuccess ? (
        <div className="text-center py-4 space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Send className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="font-medium text-gray-900">Payment Initiated</h3>
          <p className="text-sm text-gray-600">
            The recipient will receive an SMS with instructions to claim their funds at bluetoothcash.net.
          </p>
          <Button 
            onClick={() => setIsSuccess(false)} 
            variant="outline" 
            className="mt-2"
          >
            Send Another Transfer
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="tel"
              placeholder="Recipient's phone (555) 123-4567"
              value={phone}
              onChange={handlePhoneChange}
              className="w-full"
              required
            />
          </div>

          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-500">$</span>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="pl-7 w-full"
              required
            />
          </div>

          <Button 
            type="submit" 
            className="w-full flex items-center justify-center gap-2" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>Processing...</>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send Secure SMS Transfer
                <LockKeyhole className="h-4 w-4" />
              </>
            )}
          </Button>

          <div className="flex items-center justify-center mt-2 space-x-2 text-xs text-gray-500">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <p>Recipient will get SMS to claim money at bluetoothcash.net</p>
          </div>
        </form>
      )}
    </div>
  );
};

export default QuickSmsTransfer;
